/**
 * Search Autocomplete Component
 * 
 * Provides autocomplete suggestions for search input, including:
 * - Past search history
 * - Keyword recommendations from KeywordRecommendationsModal
 * - Field suggestions (title:, organization:, etc.)
 */

import React, { useState, useEffect, useRef } from 'react';
import { Search, Clock, Sparkles, Tag } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useWorkspace } from '@tracekit/core';
import { getRecommendedKeywords, getKeywordTaskStatus, KeywordTaskStatus, RecommendedKeyword } from '../api/opportunities';
import { SEARCHABLE_FIELDS } from '../utils/advancedSearchParser';
import { FilterSuggestions } from './government-data/FilterSuggestions';

interface SearchAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onSelect: (value: string) => void;
  searchHistory: string[];
  projectId?: string;
  placeholder?: string;
  className?: string;
}

interface Suggestion {
  id: string;
  text: string;
  type: 'history' | 'keyword' | 'field' | 'operator';
  icon?: React.ReactNode;
}

export function SearchAutocomplete({
  value,
  onChange,
  onSelect,
  searchHistory,
  projectId,
  placeholder = 'Search opportunities...',
  className = '',
}: SearchAutocompleteProps) {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Fetch keyword recommendations (async with polling if task_id returned)
  const [taskId, setTaskId] = useState<string | null>(null);
  const [pollingKeywords, setPollingKeywords] = useState<RecommendedKeyword[]>([]);
  
  const { data: keywordsData } = useQuery({
    queryKey: ['recommended-keywords', projectId],
    queryFn: async () => {
      const response = await getRecommendedKeywords(projectId!);
      
      // If cached, return immediately
      if (response.cached && response.keywords && response.keywords.length > 0) {
        return response;
      }
      
      // If task_id returned, start polling
      if (response.task_id) {
        setTaskId(response.task_id);
        return response;
      }
      
      return response;
    },
    enabled: !!projectId && showSuggestions,
    staleTime: 30 * 60 * 1000, // Cache for 30 minutes
  });

  // Poll for task status if task_id exists
  const { data: taskStatus } = useQuery({
    queryKey: ['keyword-task-status', taskId],
    queryFn: () => getKeywordTaskStatus(taskId!),
    enabled: !!taskId && showSuggestions,
    refetchInterval: (query) => {
      const data = query.state.data as KeywordTaskStatus | undefined;
      if (data?.status === 'completed' || data?.status === 'failed') {
        return false; // Stop polling when done
      }
      return 1000; // Poll every 1 second
    },
  });

  // Update polling keywords when task completes
  useEffect(() => {
    if (taskStatus?.status === 'completed' && taskStatus.keywords) {
      setPollingKeywords(taskStatus.keywords);
      setTaskId(null); // Clear task_id to stop polling
    }
  }, [taskStatus]);

  // Use polling keywords if available, otherwise use cached/initial data
  const keywords = pollingKeywords.length > 0 
    ? pollingKeywords 
    : keywordsData?.keywords || [];

  // Extract location/department filters (from polling data or initial cache)
  const locationKeywords = taskStatus?.location_keywords || keywordsData?.location_keywords || [];
  const departmentNames = taskStatus?.department_names || keywordsData?.department_names || [];
  const locationCount = taskStatus?.location_count || keywordsData?.location_count || 0;
  const departmentCount = taskStatus?.department_count || keywordsData?.department_count || 0;

  // Handler for filter selection
  const handleSelectFilter = (filterType: 'location' | 'department', filterValue: string) => {
    // Format as field:value syntax
    const filterText = `${filterType === 'location' ? 'city' : 'organization'}:"${filterValue}"`;
    
    // Append to existing search or replace
    const newValue = value.trim() 
      ? `${value.trim()} AND ${filterText}`
      : filterText;
    
    onChange(newValue);
    onSelect(newValue); // Trigger search immediately
    setShowSuggestions(false);
  };

  // Generate suggestions based on current input
  const suggestions = React.useMemo((): Suggestion[] => {
    const allSuggestions: Suggestion[] = [];
    const lowerValue = value.toLowerCase().trim();

    // If input is empty, show recent history and top keywords
    if (!value || value.trim() === '') {
      // Recent search history (last 5)
      searchHistory.slice(0, 5).forEach((historyItem, idx) => {
        allSuggestions.push({
          id: `history-${idx}`,
          text: historyItem,
          type: 'history',
          icon: <Clock className="h-4 w-4" />,
        });
      });

      // Top keywords (first 5)
      keywords.slice(0, 5).forEach((keyword, idx) => {
        allSuggestions.push({
          id: `keyword-${idx}`,
          text: keyword.keyword,
          type: 'keyword',
          icon: <Sparkles className="h-4 w-4" />,
        });
      });

      return allSuggestions;
    }

    // If input contains ':', suggest field completions
    if (value.includes(':') && !value.endsWith(':')) {
      const [fieldPart, valuePart] = value.split(':');
      const field = fieldPart.trim().toLowerCase();
      
      // If field is complete, suggest values based on field
      if (field && SEARCHABLE_FIELDS.includes(field as any)) {
        // Could add field-specific value suggestions here
        // For now, just return empty
        return [];
      }
    }

    // If input starts with ':', suggest fields
    if (value.startsWith(':')) {
      return SEARCHABLE_FIELDS.map((field, idx) => ({
        id: `field-${idx}`,
        text: `${field}:`,
        type: 'field',
        icon: <Tag className="h-4 w-4" />,
      }));
    }

    // Filter search history by input
    searchHistory
      .filter(item => item.toLowerCase().includes(lowerValue))
      .slice(0, 3)
      .forEach((historyItem, idx) => {
        allSuggestions.push({
          id: `history-${idx}`,
          text: historyItem,
          type: 'history',
          icon: <Clock className="h-4 w-4" />,
        });
      });

    // Filter keywords by input
    keywords
      .filter(k => k.keyword.toLowerCase().includes(lowerValue))
      .slice(0, 5)
      .forEach((keyword, idx) => {
        allSuggestions.push({
          id: `keyword-${idx}`,
          text: keyword.keyword,
          type: 'keyword',
          icon: <Sparkles className="h-4 w-4" />,
        });
      });

    // Suggest field: if input looks like it could be a field
    if (!value.includes(':') && !value.includes(' ') && value.length > 0) {
      SEARCHABLE_FIELDS
        .filter(field => field.toLowerCase().startsWith(lowerValue))
        .slice(0, 3)
        .forEach((field, idx) => {
          allSuggestions.push({
            id: `field-${idx}`,
            text: `${field}:`,
            type: 'field',
            icon: <Tag className="h-4 w-4" />,
          });
        });
    }

    // Suggest operators if input has space
    if (value.includes(' ') && !value.endsWith(' AND') && !value.endsWith(' OR') && !value.endsWith(' NOT')) {
      allSuggestions.push({
        id: 'op-and',
        text: 'AND',
        type: 'operator',
      });
      allSuggestions.push({
        id: 'op-or',
        text: 'OR',
        type: 'operator',
      });
      allSuggestions.push({
        id: 'op-not',
        text: 'NOT',
        type: 'operator',
      });
    }

    return allSuggestions.slice(0, 10); // Limit to 10 suggestions
  }, [value, searchHistory, keywords]);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestions || suggestions.length === 0) {
      if (e.key === 'Enter') {
        onSelect(value);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleSelectSuggestion(suggestions[selectedIndex]);
        } else {
          onSelect(value);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
    }
  };

  const handleSelectSuggestion = (suggestion: Suggestion) => {
    let newValue = value;
    let shouldTriggerSearch = false;

    // Handle field suggestions
    if (suggestion.type === 'field' && suggestion.text.endsWith(':')) {
      if (value.includes(':')) {
        // Replace existing field
        const parts = value.split(':');
        newValue = `${suggestion.text.replace(':', '')}:${parts.slice(1).join(':')}`;
      } else {
        newValue = suggestion.text;
      }
    } else if (suggestion.type === 'operator') {
      // Append operator with space
      newValue = `${value.trim()} ${suggestion.text} `;
    } else {
      // Replace with suggestion
      newValue = suggestion.text;
      // For history and keyword suggestions, trigger search immediately
      if (suggestion.type === 'history' || suggestion.type === 'keyword') {
        shouldTriggerSearch = true;
      }
    }

    onChange(newValue);
    setShowSuggestions(false);
    setSelectedIndex(-1);
    
    // If it's a history or keyword selection, trigger search immediately
    if (shouldTriggerSearch) {
      onSelect(newValue);
    } else {
      // Focus back on input for field/operator suggestions
      inputRef.current?.focus();
    }
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative flex-1">
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
            setShowSuggestions(true);
            setSelectedIndex(-1);
          }}
          onFocus={() => setShowSuggestions(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className={`w-full px-4 py-2 pl-10 border border-theme-border rounded-lg bg-white dark:bg-theme-paper text-theme-text focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${className}`}
        />
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-gray-500 pointer-events-none" />
      </div>

      {/* Suggestions Dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-theme-border rounded-lg shadow-lg max-h-96 overflow-y-auto"
        >
          {/* Keyword Suggestions */}
          {suggestions.map((suggestion, index) => (
            <button
              key={suggestion.id}
              type="button"
              onClick={() => handleSelectSuggestion(suggestion)}
              className={`w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center gap-2 ${
                index === selectedIndex ? 'bg-gray-100 dark:bg-gray-700' : ''
              }`}
            >
              {suggestion.icon && (
                <span className="text-gray-400 dark:text-gray-500">
                  {suggestion.icon}
                </span>
              )}
              <span className="flex-1 text-sm text-theme-text">
                {suggestion.text}
              </span>
              {suggestion.type === 'history' && (
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  Recent
                </span>
              )}
              {suggestion.type === 'keyword' && (
                <span className="text-xs text-blue-500 dark:text-blue-400">
                  Recommended
                </span>
              )}
            </button>
          ))}
          
          {/* Filter Suggestions Section */}
          {(locationKeywords.length > 0 || departmentNames.length > 0) && (
            <div className="border-t border-gray-200 dark:border-gray-700 p-3">
              <FilterSuggestions
                locationKeywords={locationKeywords}
                departmentNames={departmentNames}
                locationCount={locationCount}
                departmentCount={departmentCount}
                onSelectFilter={handleSelectFilter}
                maxVisible={8}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
